/**
 * Sequential step runner.
 *
 * `runSteps` walks an array of `FlowStep`s, dispatches each through
 * the step registry, and threads `StepResult` semantics:
 *
 * - `shouldReturn` unwinds to the caller, propagating `returnValue`.
 * - `error` (or a thrown error) triggers the step's `err` branch
 * when present, then re-throws if the branch did not return.
 *
 * Runner behaviour is intentionally minimal — handlers carry the
 * domain logic; this file is just a dispatcher.
 */

import type { FlowStep } from "../types/flow";

import type { ExecutionContext } from "../context/execution-context";
import { RuntimeAbortError, StepError } from "../errors/index";
import { getStepHandler } from "./step-registry";
import type { StepResult } from "./types";

/**
 * Execute an array of steps in order. Returns the most recent
 * `StepResult`, or `undefined` if no step produced one.
 */
export async function runSteps(
 steps: readonly FlowStep[],
 ctx: ExecutionContext,
): Promise<StepResult | void> {
 let lastResult: StepResult | void = undefined;

 for (const step of steps) {
 if (ctx.signal?.aborted) {
 throw new RuntimeAbortError();
 }

 const handler = getStepHandler(step.type);
 if (!handler) {
 ctx.logger.warn(`No handler registered for step type "${step.type}"`);
 continue;
 }

 try {
 const outcome = await handler(step, ctx);
 lastResult = outcome ?? undefined;

 if (outcome && typeof outcome === "object") {
 // Soft-failure path: handler captured an error and asked the
 // runner to dispatch its inline error branch.
 if ("error" in outcome && outcome.error !== undefined) {
 const handled = await handleStepError(
 outcome.error,
 outcome.errSteps ?? readErrSteps(step),
 ctx,
 );
 if (handled?.shouldReturn) return handled;
 continue;
 }
 if (outcome.shouldReturn) {
 return outcome;
 }
 }
 } catch (err) {
 if (err instanceof RuntimeAbortError) throw err;
 const errSteps = readErrSteps(step);
 const handled = await handleStepError(err, errSteps, ctx);
 if (handled?.shouldReturn) return handled;
 // If no inline branch consumed the error, surface it as a
 // typed StepError so callers see which step blew up.
 if (!errSteps || errSteps.length ===0) {
 throw err instanceof StepError
 ? err
 : new StepError(
 step.type,
 err instanceof Error ? err.message : String(err),
 err,
 );
 }
 }
 }

 return lastResult;
}

/**
 * Run a step's `err` branch (if any) with `@error` populated.
 * Returns a `StepResult` when the branch executed `f:return`, so the
 * caller can short-circuit the parent flow.
 */
async function handleStepError(
 err: unknown,
 errSteps: readonly FlowStep[] | undefined,
 ctx: ExecutionContext,
): Promise<StepResult | undefined> {
 if (!errSteps || errSteps.length ===0) return undefined;
 ctx.setError(err);
 try {
 const branchResult = await runSteps(errSteps, ctx);
 if (
 branchResult &&
 typeof branchResult === "object" &&
 branchResult.shouldReturn
 ) {
 return branchResult;
 }
 return undefined;
 } finally {
 // Clear error scope so subsequent siblings don't see stale data.
 ctx.setError(undefined);
 }
}

function readErrSteps(step: FlowStep): readonly FlowStep[] | undefined {
 const candidate = (step as unknown as Record<string, unknown>)["err"];
 return Array.isArray(candidate)
 ? (candidate as readonly FlowStep[])
 : undefined;
}